import type { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";

export type RecentlyCompletedAirsPublicId = { airPublicIdentifier: string; expiry: string };

export const AIR_RECENTLY_COMPLETED_STORAGE_KEY = "airs-recently-completed";

const getRecentlyCompletedAirPublicIds = (): RecentlyCompletedAirsPublicId[] | null => {
  return JSON.parse(localStorage.getItem(AIR_RECENTLY_COMPLETED_STORAGE_KEY) ?? "null");
};

const setRecentlyCompletedAirPublicIds = (airs: RecentlyCompletedAirsPublicId[]) => {
  return localStorage.setItem(AIR_RECENTLY_COMPLETED_STORAGE_KEY, JSON.stringify(airs));
};

export const addRecentlyCompletedTask = (airPublicId: string) => {
  const recentlyCompletedAirsData = getRecentlyCompletedAirPublicIds();
  const newCompletedAir = {
    airPublicIdentifier: airPublicId,
    expiry: dayjs().add(30, "minutes").toISOString(), // Approximate 30 mins as a session
  };
  setRecentlyCompletedAirPublicIds(
    recentlyCompletedAirsData ? [...recentlyCompletedAirsData, newCompletedAir] : [newCompletedAir]
  );
};

const getRecentlyCompletedTasks = () => {
  const recentlyCompletedAirsData = getRecentlyCompletedAirPublicIds();

  if (!recentlyCompletedAirsData) {
    return [];
  }

  // Remove expired recently completed airs from local storage
  const notExpiredAirs = recentlyCompletedAirsData.filter((air) => dayjs().isBefore(dayjs(air.expiry)));

  // Clean up local storage if all recently completed airs have expired
  if (notExpiredAirs.length === 0) {
    localStorage.removeItem(AIR_RECENTLY_COMPLETED_STORAGE_KEY);
    return [];
  }
  setRecentlyCompletedAirPublicIds(notExpiredAirs);

  return getRecentlyCompletedAirPublicIds() ?? [];
};

export const getRecentlyCompletedAirs = (
  allAirsData: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema[]
) => {
  const recentlyCompletedTasks = getRecentlyCompletedTasks();

  return allAirsData.filter((air) =>
    recentlyCompletedTasks.some((completedAir) => air.public_identifier === completedAir.airPublicIdentifier)
  );
};

export const clearAllRecentlyCompletedTasks = () => {
  localStorage.removeItem(AIR_RECENTLY_COMPLETED_STORAGE_KEY);
  return null;
};
